import React, { RefObject, useCallback, useEffect, useReducer, useRef } from 'react';
import { useMediaQuery } from 'react-responsive';
import clsx from 'clsx';
import { MOBILE_MAX_WIDTH } from '@app/layout/useSidebarState.tsx';
import {
  PanelDockSide,
  PanelPlacement,
  readPanelPlacement,
  setPanelMinimized,
  usePanelDockEnabled,
  usePanelMinimized,
  usePanelPlacement,
  writePanelPlacement,
} from './panelDockStore.ts';
import { floatRectFor, PanelRect } from './panelGeometry.ts';
import usePanelDrag from './usePanelDrag.ts';

interface Options {
  visible: boolean;
  onHide: () => void;
  /**
   * Leave the page behind the panel usable: no dimming, clicks and scrolling go through, and clicking
   * outside does not close it. Only while the profile switch is on — off, the panel is modal as before.
   *
   * A behaviour change, not a visual one: the user can navigate, open another panel or edit the page
   * while this one is open. Right for a helper that describes the page; wrong for most form panels.
   */
  nonModal?: boolean;
  /** Scroll lock for the modal case. Ignored when non-modal — there is nothing to lock the page for. */
  blockScroll?: boolean;
  /**
   * Allow the panel to be pulled off its edge into a floating window (see <FloatingPanel>). Needs
   * `nonModal` — a window over a dimmed, inert page would make no sense. Desktop only.
   */
  floatable?: boolean;
  /**
   * Not dockable at all: plain Sidebar props, no classes, no controls. For components such as
   * RingilSidebar that only some of their callers opt in to — hooks cannot be called conditionally.
   */
  disabled?: boolean;
}

export interface PanelDock {
  key: string;
  /** The profile switch. Off: the panel behaves like any other side panel and offers no dock controls. */
  enabled: boolean;
  minimized: boolean;
  placement: PanelPlacement;
  /** What the panel is right now — 'float' only when floating is allowed here (switch, opt-in, desktop). */
  mode: 'dock' | 'float';
  /** The docked edge; while floating, the edge it came from. Always 'right' while the switch is off. */
  side: PanelDockSide;
  canFloat: boolean;
  minimize: () => void;
  restore: () => void;
  dock: (side: PanelDockSide) => void;
  float: () => void;
  /** Spread onto the PrimeReact Sidebar in place of the caller's own visible / onHide / modal / blockScroll / position. */
  sidebarProps: { visible: boolean; onHide: () => void; modal: boolean; blockScroll: boolean; position: PanelDockSide };
  /** Merged into the Sidebar's className — drives the minimized slide-out in styles/base/sidebar.scss. */
  className: string;
  /** Spread onto the panel's header — in both modes — to make it the drag handle. Put <PanelDragGrip> in it. */
  dragHandleProps: { onPointerDown: (e: React.PointerEvent<HTMLElement>) => void; className: string };
  /** For <FloatingPanel>: whether it shows, where, and which dock a drag in progress would snap to. */
  floatVisible: boolean;
  floatRect: PanelRect;
  floatRef: RefObject<HTMLDivElement>;
  snapPreview: PanelDockSide | null;
}

/**
 * Makes a PrimeReact Sidebar dockable: to either edge (remembered per panel), optionally non-modal,
 * optionally floatable as a window, and minimizable to a pill without unmounting, so everything inside
 * it — a half-typed message, scroll position, a request in flight — survives until it is restored.
 *
 * Docking left uses PrimeReact's own `position="left"`; the placement next to the menu rail lives in
 * styles/base/sidebar.scss, mirroring the right-edge rule. Switching sides does not remount the panel.
 *
 * Floating renders the same content in <FloatingPanel> instead of the Sidebar, so the content remounts
 * when the container changes: any state it must keep has to live above it (as the assistant's chat and
 * draft do). The Sidebar vanishes at once as the window appears — no exit slide, which would read as the
 * panel closing — and slides back in when the window docks again.
 *
 * Minimizing does not touch `visible`. The panel slides off its edge and the mask stops taking the
 * pointer (modal is dropped while minimized), so the page underneath is fully usable. Render
 * <MinimizedPanelPill> next to the Sidebar to give the user the way back, and <PanelDockControls> in
 * the panel header for the way there.
 *
 * `key` identifies the panel in the store; the provider that opens the panel should call
 * `setPanelMinimized(key, false)` too, so that opening an already-open, minimized panel brings it back.
 */
const usePanelDock = (key: string, { visible, onHide, nonModal = false, blockScroll = false, floatable = false, disabled = false }: Options): PanelDock => {
  const enabled = usePanelDockEnabled() && !disabled;
  const placement = usePanelPlacement(key);
  const minimizedInStore = usePanelMinimized(key);
  const isMobile = useMediaQuery({ maxWidth: MOBILE_MAX_WIDTH });

  const canFloat = enabled && floatable && nonModal && !isMobile;
  const mode = canFloat && placement.mode === 'float' ? 'float' : 'dock';
  const minimized = enabled && visible && minimizedInStore;
  const side: PanelDockSide = enabled ? placement.side : 'right';
  const modal = !(enabled && (nonModal || minimized));

  const floatRef = useRef<HTMLDivElement>(null);
  const drag = usePanelDrag({ key, enabled: canFloat, floating: mode === 'float', floatRef });

  const minimize = useCallback(() => setPanelMinimized(key, true, side), [key, side]);
  const restore = useCallback(() => setPanelMinimized(key, false), [key]);
  // Read at call time: a drag may have written a newer placement than the one this render saw.
  const dock = useCallback((nextSide: PanelDockSide) => writePanelPlacement(key, { ...readPanelPlacement(key), mode: 'dock', side: nextSide }), [key]);
  const float = useCallback(() => {
    const current = readPanelPlacement(key);
    writePanelPlacement(key, { ...current, mode: 'float', ...floatRectFor(current) });
  }, [key]);

  // Keeps an open window on screen while the browser is resized. The stored geometry is untouched — it
  // is clamped on the way out by floatRectFor — so shrinking the browser for a moment and enlarging it
  // again puts the window back where the user left it. A re-render is all it takes; one per frame.
  const [, rerender] = useReducer((n: number) => n + 1, 0);
  const floatVisible = visible && mode === 'float';
  useEffect(() => {
    if (!floatVisible) return;
    let frame = 0;
    const onResize = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(rerender);
    };
    window.addEventListener('resize', onResize);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', onResize);
    };
  }, [floatVisible]);

  // A closed panel always reopens expanded, and so does one whose switch was just turned off.
  useEffect(() => {
    if (!visible || !enabled) setPanelMinimized(key, false);
  }, [key, visible, enabled]);
  // Also one that was unmounted while minimized — most form panels are unmounted by their parent rather
  // than hidden, and a stale flag would bring the next one up minimized (and keep a gap in the pills).
  useEffect(() => () => setPanelMinimized(key, false), [key]);

  // PrimeReact binds its Escape listener when the panel opens and does not re-read closeOnEscape, so a
  // minimized panel would still close on Escape pressed anywhere in the app — swallowed here instead.
  const guardedOnHide = useCallback(() => {
    if (!minimized) onHide();
  }, [minimized, onHide]);

  return {
    key,
    enabled,
    minimized,
    placement,
    mode,
    side,
    canFloat,
    minimize,
    restore,
    dock,
    float,
    sidebarProps: { visible: visible && mode === 'dock', onHide: guardedOnHide, modal, blockScroll: modal && blockScroll, position: side },
    // While floating, the Sidebar is only still there for its exit transition — hidden outright instead,
    // so turning into a window does not look like the panel closing (see styles/base/sidebar.scss).
    className: enabled ? clsx('panel-dockable', minimized && 'panel-dock-minimized', mode === 'float' && 'panel-dock-undocked') : '',
    // `relative` places <PanelDragGrip> on the header's top edge; `group/panel-header` lights it up while
    // the pointer is anywhere on the handle.
    dragHandleProps: { onPointerDown: drag.onPointerDown, className: canFloat ? 'group/panel-header relative cursor-grab active:cursor-grabbing' : '' },
    floatVisible,
    // Clamped to the current viewport on every render — which covers a position saved on a monitor that
    // is no longer attached, as well as the resize listener above.
    floatRect: floatRectFor(placement),
    floatRef,
    snapPreview: drag.snapPreview,
  };
};

export default usePanelDock;
